import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { getAccessToken } from "../api/token";
import { getNotificationSocket, disconnectNotificationSocket } from "../lib/socket";

export function useNotificationSocket() {
  const { seller } = useAuth();
  const token = getAccessToken();
  const qc = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!seller || !token) {
      disconnectNotificationSocket();
      return;
    }

    const socket = getNotificationSocket(token);

    const onNotification = (payload: {
      id: string;
      type: string;
      title: string;
      body: string | null;
      data?: Record<string, unknown>;
    }) => {
      // Play chime if available
      try {
        if (!audioRef.current && typeof window !== "undefined") {
          audioRef.current = new Audio("/notify.mp3");
        }
        audioRef.current?.play().catch(() => {});
      } catch {
        // audio play ignored
      }

      toast(payload.title, {
        description: payload.body ?? undefined,
        duration: 6000,
      });

      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      qc.invalidateQueries({ queryKey: ["chat-unread-count"] });
    };

    const onCount = (payload: { count: number }) => {
      qc.setQueryData(["notifications", "unread-count"], payload);
    };

    const onRead = () => qc.invalidateQueries({ queryKey: ["notifications"] });
    const onReadAll = () => qc.invalidateQueries({ queryKey: ["notifications"] });

    socket.on("notification:new", onNotification);
    socket.on("notification:count", onCount);
    socket.on("notification:read", onRead);
    socket.on("notification:readAll", onReadAll);

    if (!socket.connected) {
      socket.connect();
    }

    return () => {
      socket.off("notification:new", onNotification);
      socket.off("notification:count", onCount);
      socket.off("notification:read", onRead);
      socket.off("notification:readAll", onReadAll);
    };
  }, [seller?.accountId, token, qc]);
}
